import { useEffect, useRef, useState } from "react";
import { Alert, Spin } from "antd";
import { cornerstone, initCornerstone } from "@/lib/cornerstoneInit";
import type { CornerstoneImageLike } from "./imagingFileViewer.types";

interface CornerstoneViewportProps {
  /** Blob URL of the current slice, or null while it is still loading. */
  fileUrl: string | null;
  /**
   * Called with the decoded image once a slice is on screen. The DICOM dataset rides along on
   * `image.data`, which is how the metadata panel describes the exact frame being displayed
   * without a second request.
   */
  onImage?: (image: CornerstoneImageLike | null) => void;
}

/**
 * Fixed-size DICOM viewport for stack scrolling. Cornerstone is enabled on the
 * element **once**; slice changes only call ``displayImage`` on the same enabled
 * element (no enable/disable per slice), so scrubbing never flickers or resizes.
 * While the next slice hasn't arrived, the previous frame stays on screen under a
 * non-layout-shifting overlay spinner.
 */
export default function CornerstoneViewport({
  fileUrl,
  onImage,
}: CornerstoneViewportProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  const enabledRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  // Enable once on mount, disable once on unmount.
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    initCornerstone();
    cornerstone.enable(element);
    enabledRef.current = true;
    return () => {
      enabledRef.current = false;
      cornerstone.disable(element);
    };
  }, []);

  // Display the current slice on the already-enabled element.
  useEffect(() => {
    const element = elementRef.current;
    if (!element || !enabledRef.current || !fileUrl) return;
    let cancelled = false;
    cornerstone
      .loadAndCacheImage(`wadouri:${fileUrl}`)
      .then((image) => {
        if (!cancelled && enabledRef.current) {
          setError(null);
          cornerstone.displayImage(element, image);
          onImage?.(image as CornerstoneImageLike);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Failed to load DICOM image.");
          onImage?.(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fileUrl, onImage]);

  return (
    <div className="relative w-full bg-black" style={{ height: 480 }}>
      <div ref={elementRef} className="h-full w-full" />
      {!fileUrl && !error && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spin />
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Alert type="error" message={error} />
        </div>
      )}
    </div>
  );
}
